import React, { useEffect } from "react";
import { RCCButton, RCCCheckBox, RCCGrid, RCCIcon, RCCInput, RCCRangeSlider, RCCTooltip } from "react-common-components";
import SortableHandle from "src/components/Sortable/Handle";
import SortableItem from "src/components/Sortable/Item";
import SortableContainer from "src/components/Sortable/Container";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { useFieldArray } from "react-hook-form";
import { v4 } from "uuid";
import { IComponentChecklistsItem } from "./interface";
import { styleAddItemButton } from "./styles";
import { restrictToVerticalAxis } from "../Sortable/Container/modifiers";

const ChecklistsItem: React.FC<IComponentChecklistsItem> = ({
	disabled,
	error,
	register,
	index,
	items,
	control,
	names: {
		nameOfObject,
		nameOfItems,
		nameOfItemVerified,
		nameOfItemID,
		nameOfItemDesc,
		nameOfItemRangeSlider
	}
}) => {
	const { translate } = useTranslation();

	const { 
		fields: fieldsChecklistsItem, 
		remove: removeChecklistsItem,
		append: appendChecklistsItem,
		move: moveChecklistsItem
	} = useFieldArray({
		control,
		name: `${nameOfObject}[${index}].${nameOfItems}`
	});
	
	const handleDragEndChecklistsItens = (activeId: string, overId: string) => {
		const activeIndex = fieldsChecklistsItem.findIndex((checksItems) => checksItems.id === activeId);
		const overIndex = fieldsChecklistsItem.findIndex((checksItems) => checksItems.id === overId);		
		moveChecklistsItem(activeIndex, overIndex);
	};

	const addItemToChecklist = () => {
		const checklistItem: any = {};
		checklistItem.id = disabled ? "disabled" : v4();
		checklistItem.key = checklistItem.id;
		checklistItem[nameOfItemID] = "0";
		checklistItem[nameOfItemDesc] = "";
		checklistItem[nameOfItemVerified] = "N";
		checklistItem[nameOfItemRangeSlider] = 1;
		appendChecklistsItem(checklistItem);
	};

	const removeItemChecklists = (idx: number) => {
		removeChecklistsItem(idx);
	};

	useEffect(() => {
		appendChecklistsItem(items.map((item) => ({
			...item,
			id: disabled ? "disabled" : v4()
		})));
	}, []);
	
	return (
		<RCCGrid
			container
			item
			direction="column"
			style={{ position: "relative" }}
		>
			<RCCGrid styleInline={styleAddItemButton}>
				<RCCTooltip
					color="purple"
					text={translate("Adicionar Item")}				
				>
					<RCCButton
						color="transparent"
						icon={{
							name: "plus",
							color: "var(--purple)"
						}}
						onClick={() => addItemToChecklist()}
						disabled={disabled}
						tabIndex={-1}
					/>
				</RCCTooltip>
			</RCCGrid>
			{fieldsChecklistsItem && fieldsChecklistsItem.length > 0 && (
				<>
					<Title color="purple" size="sm">
						{translate("Itens")}
					</Title>
						
					<SortableContainer 
						items={fieldsChecklistsItem}
						onDragEnd={(activeId, overId) => handleDragEndChecklistsItens(activeId, overId)}
						modifiers={[restrictToVerticalAxis]}
					>	
						{fieldsChecklistsItem.map((itens: any, idx: number) => (
							<SortableItem id={itens.id} key={itens.key ? itens.key : `checklists-items-${itens[nameOfItemID]}`} >
								<RCCGrid
									container
									item
									alignItems="center"
									justifyContent="space-between"
									spacing={1}
								>

									<input 
										type="text"
										value={itens[nameOfItemID]}
										{...register(`${nameOfObject}[${index}].${nameOfItems}[${idx}].${nameOfItemID}`)} 
										style={{ display: "none" }} 
										readOnly
									/>

									<RCCCheckBox 
										name={`${nameOfObject}[${index}].${nameOfItems}[${idx}].${nameOfItemVerified}`}
										defaultChecked={itens[nameOfItemVerified] === "S"}
										register={register}
										disabled
										styleInline={{ label: "display: none", checkbox: "display: none" }}
										tabIndex={-1}
									/>
										
									<RCCGrid
										container
										item
										xs={12}
										sm={6}
										style={{ position: "relative" }}
									>
										<RCCInput 
											name={`${nameOfObject}[${index}].${nameOfItems}[${idx}].${nameOfItemDesc}`}
											register={register}
											label=""
											maxLength={100}
											defaultValue={itens[nameOfItemDesc]}
											error={error[nameOfItems] && error?.[nameOfItems][idx]?.[nameOfItemDesc]}
											disabled={disabled}
											styleInline="height: 34px;"
										/>
										{(itens[nameOfItemVerified] === "S" || itens[nameOfItemVerified] === true)  && (
											<RCCTooltip
												text={translate("Concluído")}
												color="green"
											>
												<RCCIcon 
													size={24}
													name="checkCircle"
													color="var(--green)"
													style={{ position: "absolute", right: "4px", bottom: "25%" }}
												/>
											</RCCTooltip>
											
										)}
									</RCCGrid>
									<RCCGrid
										container
										item
										xs={12}
										sm={3}
									>
										<RCCRangeSlider
											control={control}
											name={`${nameOfObject}[${index}].${nameOfItems}[${idx}].${nameOfItemRangeSlider}`}
											color="purple"
											label=""
											defaultValue={itens[nameOfItemRangeSlider]}
											min={1}
											max={100}
											disabled={disabled}
											valueLabelDisplay="on"
											styleInline=".MuiSlider-valueLabel > span > span{ color: var(--input-font-color); }"
										/>
									</RCCGrid>
									<RCCGrid
										container
										item
										xs={12}
										sm={3}
										justifyContent="flex-end"
									>
										<SortableHandle>
											<RCCTooltip
												color="grey"
												text={translate("Ordenar")}
											>
												<RCCButton
													color="transparent"
													icon={{
														name: "arrows",
														color: "var(--grey)"
													}}
													layout="isRounded"
													disabled={disabled}
													tabIndex={-1}
												/>
											</RCCTooltip>
										</SortableHandle>

										<RCCTooltip
											color="red"
											text={translate("Excluir")}
										>
											<RCCButton
												color="transparent"
												icon={{
													name: "trash",
													color: "var(--red)"
												}}
												layout="isRounded"
												onClick={() => removeItemChecklists(idx)}
												disabled={disabled || fieldsChecklistsItem.length === 1}
												tabIndex={-1}
											/>
										</RCCTooltip>
									</RCCGrid>
                            
								</RCCGrid>
							</SortableItem>
						))}
					</SortableContainer>
				</>
			)}
		</RCCGrid>
	);
};

export default ChecklistsItem;
